import {
    ExamDetails,
    ExamList,
    VerificationCodeSendingStatus,
    FilteredExamStatus,
    Exam, SendCodeForApproveExam, ISendCodeForApproveExam, IApproveExam, ApproveExam,
} from '../../api/ApiClient';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';
import moment from 'moment';
import {
    IExamIdBody, IExamsFilters, IFilterPart, IFromToDatePart,
} from 'components/models';

const initialState: IExamsState = {
    exams: null,
    waitingForApprovalExams: null,
    needingAttentionExamIds: [],
    examsInProcessingList: [],
    examsForSigningSovietProtocolsList: [],
    examDetails: null,
    examStarted: false,
    smsCodeStatus: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setExams(state: IExamsState, exams: IExamsState['exams']): IExamsState {
            return { ...state, exams };
        },
        setWaitingForApprovalExams(state: IExamsState, waitingForApprovalExams: IExamsState['waitingForApprovalExams']): IExamsState {
            return { ...state, waitingForApprovalExams };
        },
        setExamsInProcessing(state: IExamsState, examsInProcessingList: IExamsState['examsInProcessingList']): IExamsState {
            return { ...state, examsInProcessingList };
        },
        setNeedingAttentionExamIds(state: IExamsState, needingAttentionExamIds: string[]): IExamsState {
            return { ...state, needingAttentionExamIds };
        },
        setExamStarted(state: IExamsState, examStarted: boolean): IExamsState {
            return { ...state, examStarted };
        },
        setExamDetails(state: IExamsState, examDetails: ExamDetails): IExamsState {
            return { ...state, examDetails };
        },
        setSmsCodeStatus(state: IExamsState, smsCodeStatus: VerificationCodeSendingStatus | null): IExamsState {
            return { ...state, smsCodeStatus };
        },
        setExamsForSigningSovietProtocolsList(state: IExamsState, examsForSigningSovietProtocolsList: Exam[] | null): IExamsState {
            return { ...state, examsForSigningSovietProtocolsList };
        },
        clearExamDetails(state) {
            return { ...state, examDetails: null };
        },
    },
    effects: (dispatch: any) => ({
        async fetchExamsInProcessing() {
            const examList = await apiConnector.execApi(dispatch, api => api.getExamsInProcessing());

            this.setExamsInProcessing(examList);
        },
        async getExamFilteredList(payload: IExamsFilters & IFromToDatePart & IFilterPart) {
            const exams = await apiConnector.execApi(
                dispatch,
                api => api.getExamFilteredList(
                    payload.qualificationIds,
                    payload.attestationSubCommissionIds,
                    payload.accreditationCenterIds,
                    payload.filteredExamStatus,
                    payload.from,
                    payload.to,
                    payload.page,
                    payload.pageSize,
                    payload.sortOrder,
                    payload.sortField,
                ),
            );
            const needingAttentionExamIds = await apiConnector.execApi(
                dispatch,
                api => api.getNeedingAttentionExamIds(),
            );
            this.setExams(exams);
            this.setNeedingAttentionExamIds(needingAttentionExamIds);
        },
        async fetchExamDetails(examId: string) {
            dispatch.requests.setSmsCodeStatus(null);
            const exam = await apiConnector.execApi(dispatch, api => api.getExam(examId));

            this.setExamDetails(exam);

            return exam;
        },

        async fetchWaitingForApprovalExams(payload: IFilterPart = {}) {
            const waitingForApprovalExams = await apiConnector.execApi(dispatch, api => api.getWaitingForApprovalExams(
              payload.page,
              payload.pageSize,
              payload.sortOrder,
              payload.sortField,
            ));

            this.setWaitingForApprovalExams(waitingForApprovalExams);
        },

        async sendCodeForApproveExam(payload: IExamIdBody<ISendCodeForApproveExam>) {
            const verificationCodeSendingStatus = await apiConnector.execApi(dispatch, api => api.sendCodeForApproveExam(
              payload.examId,
              new SendCodeForApproveExam(payload.body),
            ));

            this.setSmsCodeStatus(verificationCodeSendingStatus);
        },

        async approveExam(payload: IExamIdBody<IApproveExam>) {
            const details = await apiConnector.execApi(dispatch, api => api.approveExam(
              payload.examId,
              new ApproveExam(payload.body),
            ));

            if (details) {
                this.setExamDetails(details);
            }

            return !!details;
        },
    }),
});

export interface IGetExamsList {
    qualificationIds?: string[];
    attestationSubCommissionIds?: string[];
    acIds?: string[];
    filteredExamStatus?: FilteredExamStatus | undefined;
    from?: moment.Moment;
    to?: moment.Moment;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

interface IExamsState {
    exams: ExamList | null;
    examDetails: ExamDetails | null;

    waitingForApprovalExams: ExamList | null;
    examsInProcessingList: Exam[] | null;
    examsForSigningSovietProtocolsList: Exam[] | null;
    needingAttentionExamIds: string[];
    examStarted: boolean;
    smsCodeStatus: VerificationCodeSendingStatus | null;
}

interface GetExamsQuery {
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}
